import React, { useEffect, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { Alert, Button, Card, CardBody, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { connect } from "react-redux";
import {
    addExpenseAttachment,
    deleteExpenseAttachment,
    getExpenseAttachments,
    getExpenses
} from "store/actions/Expenses";
import { RootState } from "../../store/store";
import PageMeta from "components/PageMeta";
import { GetRouterQuery } from "components/Utils/GetRouterQuery";
import { ExpenseState } from 'store/reducers/Expenses';
import TenantName from "components/TenantName";
import { getExpenseLedgerAccountLabel } from "common/LedgerAccounts";
import ExpenseEditButton from "./ExpenseEditButton";
import { Link } from "react-router-dom";
import ExpensesListAccount from "./ExpensesListAccount";
import DropzoneCustom, { DroppedFile } from "components/DropzoneCustom";
import { ExpenseAttachment } from "models/Expense";
import prettyBytes from 'pretty-bytes';
import ExpenseSuggestedEmails from "./ExpenseSuggestedEmails";
import {useOfficeTranslation} from "i18n/OfficeTranslation";
import CardHeader from "components/CardHeader";
import * as api from "services/Api";
import { ExpenseDocumentMatch } from "models/Document";
import { b64toBlob } from "common/base64toblob";
import { AccountState } from "store/reducers/Accounts";
import { getAccounts } from "store/actions/Accounts";
import ExpenseTaxClassification from "../Taxes/ExpenseTaxClassification";

interface Props {
    accountState: AccountState;
    expenseState: ExpenseState;
}

interface Actions {
    getAccounts: () => void;
    getExpenses: () => void;
    getExpenseAttachments: (id: string) => Promise<ExpenseAttachment[]>
    addExpenseAttachment: (id: string, file: DroppedFile) => Promise<ExpenseAttachment[]>
    deleteExpenseAttachment: (id: string, aid: string) => Promise<ExpenseAttachment[]>
}

interface AttachmentFile extends ExpenseAttachment {
    size?: number;
    url?: string;
    contentType?: string;
    revokeOnClose?: boolean;
}

const applicableAnalysisFields = new Set([
    "supplier_name", "supplier_email", "vat_number", "kvk_number", "iban",
    "invoice_number", "invoice_date", "total", "tax",
]);

const createAttachmentFile = (attachment: ExpenseAttachment): AttachmentFile => {
    const legacyType = attachment.content?.startsWith("data:")
        ? attachment.content.slice(5).split(";")[0]
        : undefined;
    const result: AttachmentFile = {
        ...attachment,
        url: attachment.content,
        contentType: attachment.content_type || legacyType || "application/octet-stream",
    };
    if (attachment.content?.includes(";base64,")) {
        try {
            const blob = b64toBlob(attachment.content.split(";base64,")[1], result.contentType);
            result.url = URL.createObjectURL(blob);
            result.size = blob.size;
        } catch {
            // Keep the legacy data URL as a safe fallback while migration is pending.
        }
    }
    return result;
};

export const ExpenseDetail: React.FC<Props & Actions> = ({
    accountState,
    expenseState,
    getAccounts,
    getExpenses,
    getExpenseAttachments,
    addExpenseAttachment,
    deleteExpenseAttachment,
}) => {
    const {intlLocale, t} = useOfficeTranslation();
    const [attachments, setAttachments] = useState<ExpenseAttachment[]>([]);
    const [attachmentsLoading, setAttachmentsLoading] = useState(true);
    const [attachmentsError, setAttachmentsError] = useState("");
    const [attachmentBusy, setAttachmentBusy] = useState("");
    const [attachmentPreviewBusy, setAttachmentPreviewBusy] = useState("");
    const [attachmentToView, setAttachmentToView] = useState<AttachmentFile | null>(null);
    const [attachmentToDelete, setAttachmentToDelete] = useState<ExpenseAttachment | null>(null);
    const [attachmentAnalysisToApply, setAttachmentAnalysisToApply] = useState<ExpenseAttachment | null>(null);
    const [selectedAnalysisFields, setSelectedAnalysisFields] = useState<string[]>([]);
    const [analysisApplyError, setAnalysisApplyError] = useState("");
    const [documentMatches, setDocumentMatches] = useState<ExpenseDocumentMatch[]>([]);
    const [documentActionBusy, setDocumentActionBusy] = useState("");

    const { id } = GetRouterQuery<{ id: string }>()
    const expense = expenseState.getExpenseById(id)
    const account = expense ? accountState.getAccountById(expense.account_id) : null;

    useEffect(() => {
        getAccounts()
        getExpenses()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadAttachments = () => {
        setAttachmentsLoading(true);
        setAttachmentsError("");
        return getExpenseAttachments(id)
            .then(expenseAttachments => setAttachments(expenseAttachments))
            .catch(() => setAttachmentsError(t("expenses.expenseDetail.loadAttachmentsFailed")))
            .finally(() => setAttachmentsLoading(false));
    };

    useEffect(() => {
        loadAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const attachmentFiles = useMemo(
        () => attachments.map(createAttachmentFile),
        [attachments]
    );

    const loadDocumentMatches = () => Promise.resolve().then(() => api.getExpenseDocumentMatches(id))
        .then(setDocumentMatches)
        .catch(() => setDocumentMatches([]));

    useEffect(() => {
        loadDocumentMatches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const applyAnalysis = (attachment: ExpenseAttachment) => {
        setDocumentActionBusy(attachment.id);
        setAnalysisApplyError("");
        api.applyExpenseAttachmentAnalysis(id, attachment.id, selectedAnalysisFields)
            .then(() => {
                setAttachmentAnalysisToApply(null);
                setSelectedAnalysisFields([]);
                getAccounts();
                getExpenses();
            })
            .catch(() => setAnalysisApplyError(t("expenses.expenseDetail.applyOcrFailed")))
            .finally(() => setDocumentActionBusy(""));
    };

    const analysisFieldLabel = (name: string) => {
        const labels: Record<string, string> = {
            supplier_name: t("expenses.expenseDetail.ocrFieldSupplierName"),
            vat_number: t("expenses.expenseDetail.ocrFieldVatNumber"),
            kvk_number: t("expenses.expenseDetail.ocrFieldKvkNumber"),
            iban: t("expenses.expenseDetail.ocrFieldIban"),
            supplier_email: t("expenses.expenseDetail.ocrFieldSupplierEmail"),
            invoice_number: t("expenses.expenseDetail.ocrFieldInvoiceNumber"),
            invoice_date: t("expenses.expenseDetail.ocrFieldInvoiceDate"),
            total: t("expenses.expenseDetail.ocrFieldTotal"),
            tax: t("expenses.expenseDetail.ocrFieldTax"),
        };
        return labels[name] || name.replace(/_/g, " ");
    };

    const analysisRows = useMemo(() => {
        const fields = attachmentAnalysisToApply?.analysis?.fields;
        if (!fields || !expense) return [];
        const currentValue = (name: string): string => {
            switch (name) {
                case "supplier_name": return account?.legal_name ?? "";
                case "supplier_email": return account?.email ?? "";
                case "vat_number": return account?.vat_number ?? "";
                case "kvk_number": return account?.chamber_of_commerce_number ?? "";
                case "iban": return account?.bank_accounts.map(bank => bank.iban).join(", ") ?? "";
                case "total": return expense.total.toFixed(2);
                case "tax": return expense.tax.toFixed(2);
                default: return expense.metadata?.[name] || "";
            }
        };
        return Object.entries(fields).map(([name, field]) => ({
            name,
            label: analysisFieldLabel(name),
            current: currentValue(name) || "—",
            extracted: field.value,
            confidence: field.confidence,
            evidence: field.evidence || "—",
            applicable: applicableAnalysisFields.has(name),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, attachmentAnalysisToApply, expense, t]);

    const openAnalysisReview = (attachment: ExpenseAttachment) => {
        const fields = attachment.analysis?.fields ?? {};
        const defaults: string[] = [];
        const numericDefault = (name: "total" | "tax") => {
            const field = fields[name];
            if (field && field.confidence >= .75 && Number.isFinite(Number(field.value))) defaults.push(name);
        };
        numericDefault("total");
        numericDefault("tax");
        if (fields.vat_number?.confidence >= .9 && !account?.vat_number) defaults.push("vat_number");
        if (fields.invoice_number) defaults.push("invoice_number");
        if (fields.invoice_date) defaults.push("invoice_date");
        setAnalysisApplyError("");
        setSelectedAnalysisFields(defaults);
        setAttachmentAnalysisToApply(attachment);
    };

    const closeAnalysisReview = () => {
        setAnalysisApplyError("");
        setAttachmentAnalysisToApply(null);
        setSelectedAnalysisFields([]);
    };

    const toggleAnalysisField = (name: string) => setSelectedAnalysisFields(current =>
        current.includes(name) ? current.filter(value => value !== name) : [...current, name]
    );

    const acceptDocumentMatch = (match: ExpenseDocumentMatch) => {
        setDocumentActionBusy(match.id);
        api.acceptExpenseDocumentMatch(id, match.id, true)
            .then(() => Promise.all([loadAttachments(), loadDocumentMatches(), Promise.resolve(getExpenses())]))
            .finally(() => setDocumentActionBusy(""));
    };

    const uploadAttachment = (file: DroppedFile) => {
        setAttachmentBusy("upload");
        setAttachmentsError("");
        addExpenseAttachment(id, file)
            .then(expenseAttachments => {
                setAttachments(expenseAttachments);
                getExpenses();
            })
            .catch(() => setAttachmentsError(t("expenses.expenseDetail.uploadAttachmentFailed")))
            .finally(() => setAttachmentBusy(""));
    };

    const openAttachment = (attachment: AttachmentFile) => {
        if (attachment.url) {
            setAttachmentToView(attachment);
            return;
        }

        setAttachmentPreviewBusy(attachment.id);
        setAttachmentsError("");
        api.getExpenseAttachmentContent(id, attachment.id)
            .then(content => setAttachmentToView({
                ...attachment,
                url: URL.createObjectURL(content),
                size: content.size,
                contentType: content.type || attachment.contentType,
                revokeOnClose: true,
            }))
            .catch(() => setAttachmentsError(t("expenses.expenseDetail.viewAttachmentFailed")))
            .finally(() => setAttachmentPreviewBusy(""));
    };

    const closeAttachment = () => setAttachmentToView(null);

    useEffect(() => () => {
        if (attachmentToView?.revokeOnClose && attachmentToView.url) {
            URL.revokeObjectURL(attachmentToView.url);
        }
    }, [attachmentToView]);

    const removeAttachment = () => {
        if (!attachmentToDelete) return;

        setAttachmentBusy(attachmentToDelete.id);
        setAttachmentsError("");
        deleteExpenseAttachment(id, attachmentToDelete.id)
            .then(expenseAttachments => {
                setAttachments(expenseAttachments);
                setAttachmentToDelete(null);
                getExpenses();
            })
            .catch(() => setAttachmentsError(t("expenses.expenseDetail.deleteAttachmentFailed")))
            .finally(() => setAttachmentBusy(""));
    };



    if (expense === null) {
        return <></>
    }

    return (
        <>
            <PageMeta title={t("common.expense")} />
            <div className="page-content">
                <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h2 className="mb-0 font-size-120">{expense.name}</h2>
                    <ExpenseEditButton expense={expense} />
                </div>
                <Container fluid>
                    <Row>
                        <Col lg={6}>
                            <ExpenseTaxClassification expense={expense} />
                            <Card>
                                <CardBody>
                                    <div>
                                        <div className="clearfix"></div>
                                        <p className="text-muted text-uppercase fw-semibold mb-0" style={{ fontSize: "13px" }}>{t("common.details")}</p>
                                        <hr />
                                        <table className="table table-borderless align-middle table-centered table mb-0">
                                            <tbody>
                                                <tr>
                                                    <td>{t("common.date")}</td>
                                                    <td>{expense.GetDate().toLocaleDateString(intlLocale)}</td>
                                                </tr>
                                                <tr>
                                                    <td>{t("common.tax")}</td>
                                                    <td>&euro; {expense.tax.toFixed(2)}</td>
                                                </tr>
                                                <tr>
                                                    <td>{t("expenses.expenseDetail.supplierName")}</td>
                                                    <td><Link to={`/accounts/${expense.account_id}`}>{account?.legal_name ?? t("common.unknownSupplier")}</Link></td>
                                                </tr>
                                                <tr>
                                                    <td>{t("expenses.expenseDetail.supplierVat")}</td>
                                                    <td>{account?.vat_number || <span className="text-muted">{t("common.unknown")}</span>}</td>
                                                </tr>
                                                <tr>
                                                    <td>{t("common.tenant")}</td>
                                                    <td><Link to={`/tenants/${expense.tenant}`}><TenantName id={expense.tenant} /></Link></td>
                                                </tr>
                                                <tr>
                                                    <td>{t("common.ledgerAccount")}</td>
                                                    <td>{getExpenseLedgerAccountLabel(expense, t)}</td>
                                                </tr>
                                                <tr>
                                                    <td>{t("common.total")}</td>
                                                    <td>&euro; {expense.total.toFixed(2)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                </CardBody>
                            </Card>
                            <Card>
                                <CardBody>
                                    <CardHeader
                                        title={t("expenses.expenseDetail.receiptsAndAttachments")}
                                        icon="mdi-paperclip"
                                        count={attachments.length}
                                    />
                                    {attachmentsError && <Alert color="danger" fade={false} className="mt-3 mb-0">{attachmentsError}</Alert>}
                                    {attachmentsLoading && <div className="text-center py-4"><Spinner size="sm" /></div>}
                                    {!attachmentsLoading && attachmentFiles.length === 0 && !attachmentsError &&
                                        <p className="text-muted mt-3 mb-0">{t("expenses.expenseDetail.noAttachments")}</p>
                                    }
                                    {!attachmentsLoading && attachmentFiles.map(attachment =>
                                        <div key={attachment.id} className="d-flex align-items-center justify-content-between gap-3 border-bottom py-3">
                                            <div className="d-flex align-items-center gap-2 min-w-0">
                                                <i className="mdi mdi-file-document-outline text-primary font-size-20" />
                                                <div className="min-w-0">
                                                    <span className="d-block text-truncate">{attachment.name}</span>
                                                    {attachment.size !== undefined && <small className="text-muted">{prettyBytes(attachment.size)}</small>}
                                                    {attachment.status && attachment.status !== "completed" && <small className="d-block text-muted">{t("expenses.expenseDetail.ocrStatus", {status: attachment.status})}</small>}
                                                </div>
                                            </div>
                                            <div className="d-flex flex-wrap justify-content-end gap-2">
                                                {attachment.analysis && <Button
                                                    type="button"
                                                    size="sm"
                                                    color="success"
                                                    outline
                                                    disabled={documentActionBusy !== ""}
                                                    onClick={() => openAnalysisReview(attachment)}
                                                >
                                                    {t("expenses.expenseDetail.reviewOcr")}
                                                </Button>}
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    color="primary"
                                                    outline
                                                    aria-label={t("expenses.expenseDetail.viewAttachmentName", {name: attachment.name})}
                                                    disabled={attachmentPreviewBusy !== ""}
                                                    onClick={() => openAttachment(attachment)}
                                                >
                                                    {attachmentPreviewBusy === attachment.id
                                                        ? <Spinner size="sm" />
                                                        : <><i className="mdi mdi-eye-outline me-1" />{t("expenses.expenseDetail.viewAttachment")}</>
                                                    }
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    color="danger"
                                                    outline
                                                    aria-label={t("expenses.expenseDetail.deleteAttachmentName", {name: attachment.name})}
                                                    disabled={attachmentBusy !== ""}
                                                    onClick={() => setAttachmentToDelete(attachment)}
                                                >
                                                    {attachmentBusy === attachment.id
                                                        ? <Spinner size="sm" />
                                                        : <><i className="mdi mdi-delete-outline me-1" />{t("common.delete")}</>
                                                    }
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="mt-3">
                                        <p className="text-muted small mb-2">{t("expenses.expenseDetail.uploadReceipt")}</p>
                                        <DropzoneCustom mini={true} onDrop={uploadAttachment} />
                                        {attachmentBusy === "upload" && <div className="text-center mt-2"><Spinner size="sm" /></div>}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={6}>
                            <Card>
                                <CardBody>
                                    <div>
                                        <div className="clearfix"></div>
                                        <p className="text-muted text-uppercase fw-semibold mb-0" style={{ fontSize: "13px" }}>{t("expenses.expenseDetail.allExpensesToName", {name: account?.legal_name ?? t("common.unknownSupplier")})}</p>
                                        <hr />
                                        <ExpensesListAccount accountId={expense.account_id} />
                                    </div>

                                </CardBody>
                            </Card>

                            <ExpenseSuggestedEmails expense={expense} callback={() => {
                                return loadAttachments()
                            }} />

                            {documentMatches.length > 0 && <Card>
                                <CardBody>
                                    <CardHeader title={t("expenses.expenseDetail.invoiceMatches")} icon="mdi-file-find-outline" count={documentMatches.length} />
                                    {documentMatches.map(match => <div key={match.id} className="border-bottom py-3">
                                        <div className="d-flex justify-content-between gap-3">
                                            <div>
                                                <strong>{Math.round(match.score * 100)}% {t("expenses.expenseDetail.match")}</strong>
                                                <div className="small text-muted">{match.reasons.join(", ")}</div>
                                                {match.analysis?.fields.invoice_number?.value && <div className="small">{match.analysis.fields.invoice_number.value}</div>}
                                            </div>
                                            <Button color="primary" size="sm" disabled={documentActionBusy !== ""} onClick={() => acceptDocumentMatch(match)}>
                                                {documentActionBusy === match.id ? <Spinner size="sm" /> : t("expenses.expenseDetail.acceptMatch")}
                                            </Button>
                                        </div>
                                    </div>)}
                                </CardBody>
                            </Card>}
                        </Col>
                    </Row>
                </Container>
            </div>
            <Modal isOpen={attachmentToView !== null} toggle={closeAttachment} size="xl" scrollable>
                <ModalHeader toggle={closeAttachment}>{attachmentToView?.name}</ModalHeader>
                <ModalBody>
                    {attachmentToView?.url && attachmentToView.contentType?.startsWith("image/") &&
                        <img
                            src={attachmentToView.url}
                            alt={attachmentToView.name}
                            className="img-fluid d-block mx-auto"
                        />
                    }
                    {attachmentToView?.url && attachmentToView.contentType === "application/pdf" &&
                        <iframe
                            src={attachmentToView.url}
                            title={attachmentToView.name}
                            className="w-100 border-0"
                            style={{ minHeight: "70vh" }}
                        />
                    }
                    {attachmentToView?.url && attachmentToView.contentType !== "application/pdf" && !attachmentToView.contentType?.startsWith("image/") &&
                        <p className="text-muted mb-0">{t("common.previewUnavailable")}</p>
                    }
                </ModalBody>
                <ModalFooter>
                    {attachmentToView?.url && <Button
                        tag="a"
                        color="primary"
                        outline
                        href={attachmentToView.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="mdi mdi-open-in-new me-1" />{t("expenses.expenseDetail.openInNewTab")}
                    </Button>}
                    <Button color="secondary" onClick={closeAttachment}>{t("common.close")}</Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={attachmentToDelete !== null} toggle={() => setAttachmentToDelete(null)}>
                <ModalHeader toggle={() => setAttachmentToDelete(null)}>{t("expenses.expenseDetail.deleteAttachment")}</ModalHeader>
                <ModalBody>
                    {t("common.deleteNameThisCannotBeUndone", {name: attachmentToDelete?.name ?? ""})}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setAttachmentToDelete(null)} disabled={attachmentBusy !== ""}>{t("common.cancel")}</Button>
                    <Button color="danger" onClick={removeAttachment} disabled={attachmentBusy !== ""}>
                        {attachmentBusy ? <Spinner size="sm" /> : t("common.delete")}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={attachmentAnalysisToApply !== null} toggle={closeAnalysisReview} size="xl" scrollable>
                <ModalHeader toggle={closeAnalysisReview}>
                    {t("expenses.expenseDetail.ocrReviewTitle", {name: attachmentAnalysisToApply?.name ?? ""})}
                </ModalHeader>
                <ModalBody>
                    {analysisApplyError && <Alert color="danger" fade={false}>{analysisApplyError}</Alert>}
                    <h6>{t("expenses.expenseDetail.chooseOcrFields")}</h6>
                    <Alert color={selectedAnalysisFields.length > 0 ? "success" : "info"} fade={false}>
                        {selectedAnalysisFields.length > 0
                            ? t("expenses.expenseDetail.ocrFieldsSelected", {count: selectedAnalysisFields.length})
                            : t("expenses.expenseDetail.noOcrFieldsSelected")}
                    </Alert>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead><tr>
                                <th className="text-center">{t("expenses.expenseDetail.applyOcr")}</th>
                                <th>{t("expenses.expenseDetail.ocrField")}</th>
                                <th>{t("expenses.expenseDetail.currentValue")}</th>
                                <th>{t("expenses.expenseDetail.extractedValue")}</th>
                                <th>{t("expenses.expenseDetail.confidence")}</th>
                                <th>{t("expenses.expenseDetail.evidence")}</th>
                            </tr></thead>
                            <tbody>{analysisRows.map(row =>
                                <tr key={row.name} className={selectedAnalysisFields.includes(row.name) ? "table-success" : ""}>
                                    <td className="text-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            aria-label={t("expenses.expenseDetail.selectOcrField", {field: row.label})}
                                            checked={selectedAnalysisFields.includes(row.name)}
                                            disabled={!row.applicable || documentActionBusy !== ""}
                                            onChange={() => toggleAnalysisField(row.name)}
                                        />
                                    </td>
                                    <td>{row.label}</td>
                                    <td className="text-muted">{row.current}</td>
                                    <td><strong>{row.extracted}</strong></td>
                                    <td>{Math.round(row.confidence * 100)}%</td>
                                    <td className="text-muted">{row.evidence}</td>
                                </tr>
                            )}</tbody>
                        </table>
                    </div>
                    {(attachmentAnalysisToApply?.analysis?.tax_lines?.length ?? 0) > 0 && <div className="mt-4">
                        <h6>{t("expenses.expenseDetail.taxLines")}</h6>
                        {attachmentAnalysisToApply?.analysis?.tax_lines.map((line, index) =>
                            <div key={`${line.rate}-${index}`} className="small">
                                {line.rate}%: € {line.tax.toFixed(2)} ({Math.round(line.confidence * 100)}%)
                            </div>
                        )}
                    </div>}
                    {(attachmentAnalysisToApply?.analysis?.warnings?.length ?? 0) > 0 && <Alert color="warning" fade={false} className="mt-4 mb-0">
                        <strong>{t("expenses.expenseDetail.ocrWarnings")}</strong>
                        <ul className="mb-0 mt-1">
                            {attachmentAnalysisToApply?.analysis?.warnings.map(warning => <li key={warning}>{warning}</li>)}
                        </ul>
                    </Alert>}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeAnalysisReview} disabled={documentActionBusy !== ""}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="success"
                        onClick={() => attachmentAnalysisToApply && applyAnalysis(attachmentAnalysisToApply)}
                        disabled={documentActionBusy !== "" || selectedAnalysisFields.length === 0}
                    >
                        {documentActionBusy
                            ? <Spinner size="sm" />
                            : t("expenses.expenseDetail.applySelectedOcr", {count: selectedAnalysisFields.length})
                        }
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        accountState: state.Accounts,
        expenseState: state.Expenses
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ getAccounts, getExpenses, getExpenseAttachments, addExpenseAttachment, deleteExpenseAttachment }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseDetail);
